import React, { useState } from 'react';
import { Trash2, Plus, Package, Calculator, MapPin, Weight, Shield, ShieldCheck, Sparkles } from 'lucide-react';
import { useMyContext } from '../../../../context/MyContext';
import axios from 'axios';
import Sidebar from '../../../../component/b2b/sideBar/b2bSideBar';
import NavBar from '../../../../component/b2b/navBar/b2bNavBar';
import '../../../../css/custom-scrollbar.css';
import { Base_Url, getB2bpIncode, b2bPriceEstimator } from '../../../../config/config';
import { toast } from 'react-toastify';
import B2BViewRateForward from '../../../../component/b2b/models/priceEstimatorModelB2b';

const B2bRateCalculator = () => {
    const { isDarkMode, isToggleSidebar, selectedCft } = useMyContext();
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [formData, setFormData] = useState({
        originPincode: '',
        destinationPincode: '',
        insurance: 'Owner',
        totalWeight: '',
        cod_amount: '',
        totalBoxes: '',
        invoiceValue: '',
        paymentMode: '',
        pickupStatus: ''
    });

    const [boxes, setBoxes] = useState([]);
    const [originCity, setOriginCity] = useState('');
    const [destinationCity, setDestinationCity] = useState('');
    const [loading, setLoading] = useState(false);
    const [estimateData, setEstimateData] = useState([]);
    const [originODA, setOriginODA] = useState(null);
    const [destinationODA, setDestinationODA] = useState(null);

    const handleViewClick = () => {
        setIsPopupOpen(true);
    };

    const handleClose = () => {
        setIsPopupOpen(false);
    };


    const fetchPincodeData = async (pincode, type) => {
        const token = localStorage.getItem("token");
        const cus_id = localStorage.getItem("cus_id");
        const urlgetPincode = `${Base_Url}${getB2bpIncode}?pincode=${pincode}`;
        try {
            const response = await fetch(urlgetPincode, {
                method: "GET",
                headers: {
                    token: token,
                    cus_id: cus_id,
                    "Content-Type": "application/json",
                },
            });
            const data = await response.json();
            if (data && data.status === "SUCCESS" && data.data) {
                let { city, state, oda } = data.data;

                city = city.charAt(0).toUpperCase() + city.slice(1).toLowerCase();
                state = state.charAt(0).toUpperCase() + state.slice(1).toLowerCase();

                if (type === "origin") {
                    setOriginCity(city);
                    setOriginODA(oda);
                } else if (type === "destination") {
                    setDestinationCity(city);
                    setDestinationODA(oda);
                }
                return { city, state, oda, success: true };
            }
            else if (data && data.message === "Unauthorized access - Invalid token") {
                toast.error(data.message + " Please login Again");
                return { success: false, error: "unauthorized" };
            }
            else {
                toast.error("Failed to fetch pincode details");
                return { success: false, error: "failed" };
            }
        } catch (error) {
            console.error("Error fetching pincode data:", error);
            toast.error("Error fetching pincode data");
            return { success: false, error: "network" };
        }
    };

    const handleInputChange = async (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (field === 'originPincode') {
            setOriginODA(null);
            if (value.length === 6) {
                setLoading(true);
                await fetchPincodeData(value, 'origin');
                setLoading(false);
            } else {
                setOriginCity('');
            }
        }

        if (field === 'destinationPincode') {
            setDestinationODA(null);
            if (value.length === 6) {
                setLoading(true);
                await fetchPincodeData(value, 'destination');
                setLoading(false);
            } else {
                setDestinationCity('');
            }
        }

        if (field === 'totalBoxes') {
            const num = parseInt(value, 10);
            if (!value || isNaN(num) || num <= 0) {
                setBoxes([]);
            } else {
                setBoxes(prev => {
                    if (prev.length === 0) {
                        return Array.from({ length: num }, (_, i) => ({
                            id: Date.now() + i,
                            quantity: '',
                            length: '',
                            width: '',
                            height: ''
                        }));
                    }

                    if (num > prev.length) {
                        const needed = num - prev.length;
                        const extra = Array.from({ length: needed }, (_, i) => ({
                            id: Date.now() + i + Math.random(),
                            quantity: '',
                            length: '',
                            width: '',
                            height: ''
                        }));
                        return [...prev, ...extra];
                    }

                    if (num < prev.length) {
                        return prev.slice(0, num);
                    }

                    return prev;
                });
            }
        }
    };

    const addBoxSet = () => {
        const totalBoxesUsed = boxes.reduce((sum, box) => sum + parseInt(box.quantity || 0, 10), 0);
        const remainingBoxes = parseInt(formData.totalBoxes || 0, 10) - totalBoxesUsed;

        if (remainingBoxes > 0) {
            setBoxes(prev => [...prev, {
                id: Date.now(),
                quantity: '',
                length: '',
                width: '',
                height: ''
            }]);
        }
    };

    const removeBoxSet = (id) => {
        setBoxes(prev => prev.filter(box => box.id !== id));
    };

    const updateBoxSet = (id, field, value) => {
        setBoxes(prev => {
            const index = prev.findIndex(box => box.id === id);
            if (index === -1) return prev;

            if (field !== 'quantity') {
                return prev.map((box, i) => i === index ? { ...box, [field]: value } : box);
            }

            // User cleared Qty field, allow empty string so user can type
            if (value === '') {
                return prev.map((box, i) => i === index ? { ...box, quantity: '' } : box);
            }

            const inputQty = parseInt(value, 10);
            if (isNaN(inputQty) || inputQty < 1) {
                return prev.map((box, i) => i === index ? { ...box, quantity: value } : box);
            }

            const totalBoxes = parseInt(formData.totalBoxes || 0, 10);
            if (totalBoxes <= 0) {
                return prev.map((box, i) => i === index ? { ...box, quantity: String(inputQty) } : box);
            }

            // Sum of quantities of boxes before this one
            const prevSum = prev
                .slice(0, index)
                .reduce((sum, b) => sum + (parseInt(b.quantity, 10) || 1), 0);

            const maxAllowed = Math.max(1, totalBoxes - prevSum);
            const targetQty = Math.min(inputQty, maxAllowed);

            // Remaining boxes needed across all boxes after this one
            const remainingNeeded = totalBoxes - (prevSum + targetQty);
            const afterBoxes = prev.slice(index + 1);

            let newAfterBoxes = [];
            if (remainingNeeded > 0) {
                if (afterBoxes.length >= remainingNeeded) {
                    newAfterBoxes = afterBoxes.slice(0, remainingNeeded);
                } else {
                    const extraNeeded = remainingNeeded - afterBoxes.length;
                    const extraBoxes = Array.from({ length: extraNeeded }, (_, i) => ({
                        id: Date.now() + i + Math.random(),
                        quantity: '',
                        length: '',
                        width: '',
                        height: ''
                    }));
                    newAfterBoxes = [...afterBoxes, ...extraBoxes];
                }
            }

            return [
                ...prev.slice(0, index),
                { ...prev[index], quantity: String(targetQty) },
                ...newAfterBoxes
            ];
        });
    };

    const getBoxMaxQuantity = (index) => {
        const total = parseInt(formData.totalBoxes || 0, 10);
        if (total <= 0) return 1;
        const prevSum = boxes.slice(0, index).reduce((sum, b) => sum + (parseInt(b.quantity, 10) || 1), 0);
        return Math.max(1, total - prevSum);
    };

    const getTotalBoxesUsed = () => {
        return boxes.reduce((sum, box) => sum + parseInt(box.quantity || 0, 10), 0);
    };

    const getRemainingBoxes = () => {
        return parseInt(formData.totalBoxes || 0, 10) - getTotalBoxesUsed();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        for (const box of boxes) {
            if (
                !box.length ||
                !box.width ||
                !box.height ||
                box.length === "" ||
                box.width === "" ||
                box.height === ""
            ) {
                toast.error("Please enter Length, Width, and Height for all boxes.");
                return;
            }
        }
        const payload = {
            retailer_id: parseInt(localStorage.getItem("cus_id")),
            origin_pincode: formData.originPincode,
            destination_pincode: formData.destinationPincode,
            insorance: formData.insurance,
            weight: parseFloat(formData.totalWeight),
            payment_Mode: formData.paymentMode,
            pickup_status: formData.pickupStatus,
            cft: selectedCft,
            invoice_value: parseFloat(formData.invoiceValue || 0),
            dimensions: boxes.map((box) => ({
                quantity: parseInt(box.quantity),
                total_length: parseFloat(box.length),
                total_width: parseFloat(box.width),
                total_height: parseFloat(box.height),
            })),
        };

        try {
            const token = localStorage.getItem("token");
            const cus_id = localStorage.getItem("cus_id");
            const response = await axios.post(
                Base_Url + b2bPriceEstimator,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        token: token,
                        cus_id: cus_id,
                    },
                }
            );

            if (response.data.status === "success") {
                toast.success(response.data.message);
                setEstimateData(response.data);
                handleViewClick();
            } else {
                toast.error(response.data.message);
            }
        } catch (error) {
            toast.error("Something went wrong while fetching rates");
            if (error.response?.data?.messages) {
                const messages = error.response.data.messages;

                Object.values(messages).forEach((msg) => {
                    toast.error(msg);
                });
            } else if (error.response?.data?.message) {
                toast.error(error.response.data.message);
            } else {
                toast.error("Something went wrong while fetching rates");
            }
        } finally {
            setLoading(false);
        }
    };

    const glass =
        "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
        "border border-white/60 dark:border-white/10 " +
        "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";

    return (
        <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'dark:bg-black' : 'bg-gray-50'}`}>
            <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-red-200/30 dark:bg-red-500/10 blur-3xl" />

            <NavBar />

            <div className="flex flex-1 overflow-hidden">
                <div
                    className={`${isToggleSidebar ? "md:w-64" : "md:w-20"} w-0 m-0 md:m-3 md:rounded-3xl ${glass} overflow-visible md:overflow-hidden transition-all duration-300 ease-in-out flex-shrink-0`}
                >
                    <Sidebar />
                </div>
                <div className="flex-1 min-h-0 overflow-hidden bg-slate-100/70 dark:bg-slate-950 p-2.5 sm:p-3">
                    <div className="grid lg:grid-cols-3 gap-3 sm:gap-3.5 h-full">

                        {/* Main Form Panel — scrolls internally */}
                        <div className="lg:col-span-2 h-full overflow-hidden">
                            <div className={`${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200/90'} h-full rounded-2xl sm:rounded-3xl shadow-xl shadow-slate-200/50 dark:shadow-black/40 border flex flex-col overflow-hidden`}>

                                {/* Panel header */}
                                <div className={`shrink-0 px-5 py-3.5 border-b ${isDarkMode ? 'border-slate-800 bg-slate-900/90' : 'border-slate-100 bg-gradient-to-r from-red-50/50 via-white to-rose-50/30'} flex items-center justify-between`}>
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-red-600 via-rose-600 to-red-600 flex items-center justify-center text-white shadow-md shadow-red-600/25 shrink-0">
                                            <Calculator className="w-4 h-4" />
                                        </div>
                                        <div>
                                            <h2 className={`text-sm sm:text-base font-bold tracking-normal ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                                Rate Calculator & Price Estimator
                                            </h2>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 font-normal mt-0.5">
                                                Instant B2B cargo freight pricing with live carrier rates
                                            </p>
                                        </div>
                                    </div>
                                    {selectedCft && (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-semibold bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700/40 shadow-2xs">
                                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                                            CFT: {selectedCft}
                                        </span>
                                    )}
                                </div>

                                {/* Scrollable form body */}
                                <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-5">
                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        {/* 1. Pickup & Delivery Section */}
                                        <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-slate-800/40 border-slate-800' : 'bg-slate-50/70 border-slate-200/80'} space-y-3`}>
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded-lg bg-red-500/15 text-red-600 dark:text-red-400 flex items-center justify-center">
                                                        <MapPin className="w-3.5 h-3.5" />
                                                    </div>
                                                    <h3 className={`text-xs sm:text-sm font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                                        Pickup & Delivery Pincodes
                                                    </h3>
                                                </div>
                                                <span className="text-xs font-medium text-slate-400">Step 1 of 3</span>
                                            </div>

                                            <div className="grid md:grid-cols-2 gap-3.5">
                                                {/* Origin Pincode */}
                                                <div className="space-y-1.5">
                                                    <label className={`block text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                        Origin Pincode <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="relative">
                                                        <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                                        <input
                                                            type="text"
                                                            maxLength="6"
                                                            value={formData.originPincode}
                                                            onChange={(e) => handleInputChange('originPincode', e.target.value.replace(/[^0-9]/g, ''))}
                                                            className={`w-full pl-9 pr-3.5 py-2.5 text-sm font-normal rounded-xl border transition-all duration-200 outline-none placeholder:text-slate-400 ${
                                                                isDarkMode
                                                                    ? 'bg-slate-800 border-slate-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                                                                    : 'bg-white border-slate-200 text-slate-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/10'
                                                            }`}
                                                            placeholder="Enter 6-digit origin pincode"
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                    {loading && formData.originPincode.length === 6 && (
                                                        <p className="text-xs text-red-500 flex items-center gap-1.5 font-normal mt-1">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Fetching location...
                                                        </p>
                                                    )}
                                                    {originCity && (
                                                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 text-xs font-semibold border border-red-200/60 dark:border-red-800/40 mt-1">
                                                            <MapPin size={12} /> {originCity}
                                                        </div>
                                                    )}
                                                    {Number(originODA) === 1 && (
                                                        <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 text-xs font-semibold border border-amber-200/60 dark:border-amber-800/40 mt-1 ml-1.5">
                                                            ⚠️ ODA Location
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Destination Pincode */}
                                                <div className="space-y-1.5">
                                                    <label className={`block text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                        Destination Pincode <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="relative">
                                                        <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                                                        <input
                                                            type="text"
                                                            maxLength="6"
                                                            value={formData.destinationPincode}
                                                            onChange={(e) => handleInputChange('destinationPincode', e.target.value.replace(/[^0-9]/g, ''))}
                                                            className={`w-full pl-9 pr-3.5 py-2.5 text-sm font-normal rounded-xl border transition-all duration-200 outline-none placeholder:text-slate-400 ${
                                                                isDarkMode
                                                                    ? 'bg-slate-800 border-slate-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                                                                    : 'bg-white border-slate-200 text-slate-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/10'
                                                            }`}
                                                            placeholder="Enter 6-digit destination pincode"
                                                            disabled={loading}
                                                        />
                                                    </div>
                                                    {loading && formData.destinationPincode.length === 6 && (
                                                        <p className="text-xs text-red-500 flex items-center gap-1.5 font-normal mt-1">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Fetching location...
                                                        </p>
                                                    )}
                                                    {destinationCity && (
                                                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 text-xs font-semibold border border-red-200/60 dark:border-red-800/40 mt-1">
                                                            <MapPin size={12} /> {destinationCity}
                                                        </div>
                                                    )}
                                                    {Number(destinationODA) === 1 && (
                                                        <div className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 text-xs font-semibold border border-amber-200/60 dark:border-amber-800/40 mt-1 ml-1.5">
                                                            ⚠️ ODA Location
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* 2. Insurance Section */}
                                        <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-slate-800/40 border-slate-800' : 'bg-slate-50/70 border-slate-200/80'} space-y-3`}>
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-lg bg-red-500/15 text-red-600 dark:text-red-400 flex items-center justify-center">
                                                    <Shield className="w-3.5 h-3.5" />
                                                </div>
                                                <h3 className={`text-xs sm:text-sm font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                                    Insurance Coverage
                                                </h3>
                                            </div>

                                            <div className="grid md:grid-cols-2 gap-3.5">
                                                <label
                                                    className={`relative flex items-center gap-3.5 p-3.5 rounded-2xl border cursor-pointer transition-all duration-200 ${
                                                        formData.insurance === 'Owner'
                                                            ? 'bg-red-50/70 dark:bg-red-950/30 border-red-500 ring-2 ring-red-500/20 shadow-xs'
                                                            : isDarkMode
                                                            ? 'bg-slate-800/80 border-slate-700/80 hover:border-slate-600'
                                                            : 'bg-white border-slate-200 hover:border-slate-300 shadow-2xs'
                                                    }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="insurance"
                                                        value="Owner"
                                                        checked={formData.insurance === 'Owner'}
                                                        onChange={(e) => handleInputChange('insurance', e.target.value)}
                                                        className="sr-only"
                                                    />
                                                    <div
                                                        className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-all ${
                                                            formData.insurance === 'Owner'
                                                                ? 'border-red-600 bg-red-600 text-white'
                                                                : 'border-slate-300 dark:border-slate-600 bg-transparent'
                                                        }`}
                                                    >
                                                        {formData.insurance === 'Owner' && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                                                    </div>
                                                    <div>
                                                        <span className={`text-xs sm:text-sm font-semibold block ${formData.insurance === 'Owner' ? 'text-red-700 dark:text-red-300' : isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                                            Owner Risk
                                                        </span>
                                                        <p className="text-xs text-slate-500 dark:text-slate-400 font-normal mt-0.5">Customer bears cargo risk</p>
                                                    </div>
                                                </label>

                                                <label
                                                    className={`relative flex items-center gap-3.5 p-3.5 rounded-2xl border cursor-pointer transition-all duration-200 ${
                                                        formData.insurance === 'Carrier'
                                                            ? 'bg-red-50/70 dark:bg-red-950/30 border-red-500 ring-2 ring-red-500/20 shadow-xs'
                                                            : isDarkMode
                                                            ? 'bg-slate-800/80 border-slate-700/80 hover:border-slate-600'
                                                            : 'bg-white border-slate-200 hover:border-slate-300 shadow-2xs'
                                                    }`}
                                                >
                                                    <input
                                                        type="radio"
                                                        name="insurance"
                                                        value="Carrier"
                                                        checked={formData.insurance === 'Carrier'}
                                                        onChange={(e) => handleInputChange('insurance', e.target.value)}
                                                        className="sr-only"
                                                    />
                                                    <div
                                                        className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-all ${
                                                            formData.insurance === 'Carrier'
                                                                ? 'border-red-600 bg-red-600 text-white'
                                                                : 'border-slate-300 dark:border-slate-600 bg-transparent'
                                                        }`}
                                                    >
                                                        {formData.insurance === 'Carrier' && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                                                    </div>
                                                    <div>
                                                        <span className={`text-xs sm:text-sm font-semibold flex items-center gap-1.5 ${formData.insurance === 'Carrier' ? 'text-red-700 dark:text-red-300' : isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                                                            <ShieldCheck className="w-4 h-4 text-emerald-500" /> Carrier Risk
                                                        </span>
                                                        <p className="text-xs text-slate-500 dark:text-slate-400 font-normal mt-0.5">Carrier bears cargo risk</p>
                                                    </div>
                                                </label>
                                            </div>
                                        </div>

                                        {/* 3. Weight and Boxes Section */}
                                        <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-slate-800/40 border-slate-800' : 'bg-slate-50/70 border-slate-200/80'} space-y-3`}>
                                            <div className="flex items-center gap-2">
                                                <div className="w-6 h-6 rounded-lg bg-red-500/15 text-red-600 dark:text-red-400 flex items-center justify-center">
                                                    <Weight className="w-3.5 h-3.5" />
                                                </div>
                                                <h3 className={`text-xs sm:text-sm font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                                    Weight & Package Specifications
                                                </h3>
                                            </div>

                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3.5">
                                                <div className="space-y-1.5">
                                                    <label className={`block text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                        Total Weight (kg) <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={formData.totalWeight}
                                                        onChange={(e) => handleInputChange('totalWeight', e.target.value)}
                                                        className={`w-full px-3.5 py-2.5 text-sm font-normal rounded-xl border transition-all duration-200 outline-none placeholder:text-slate-400 ${
                                                            isDarkMode
                                                                ? 'bg-slate-800 border-slate-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                                                                : 'bg-white border-slate-200 text-slate-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/10'
                                                        }`}
                                                        placeholder="Enter weight in kg"
                                                    />
                                                </div>

                                                <div className="space-y-1.5">
                                                    <label className={`block text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                        Total Boxes <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={formData.totalBoxes}
                                                        onChange={(e) => handleInputChange('totalBoxes', e.target.value)}
                                                        className={`w-full px-3.5 py-2.5 text-sm font-normal rounded-xl border transition-all duration-200 outline-none placeholder:text-slate-400 ${
                                                            isDarkMode
                                                                ? 'bg-slate-800 border-slate-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                                                                : 'bg-white border-slate-200 text-slate-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/10'
                                                        }`}
                                                        placeholder="Number of boxes"
                                                    />
                                                </div>

                                                <div className="space-y-1.5">
                                                    <label className={`block text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                        Invoice Value (₹)
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={formData.invoiceValue}
                                                        onChange={(e) => handleInputChange('invoiceValue', e.target.value)}
                                                        className={`w-full px-3.5 py-2.5 text-sm font-normal rounded-xl border transition-all duration-200 outline-none placeholder:text-slate-400 ${
                                                            isDarkMode
                                                                ? 'bg-slate-800 border-slate-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                                                                : 'bg-white border-slate-200 text-slate-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/10'
                                                        }`}
                                                        placeholder="Declared value"
                                                    />
                                                </div>

                                                <div className="space-y-1.5">
                                                    <label className={`block text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                        Payment Mode
                                                    </label>
                                                    <select
                                                        value={formData.paymentMode}
                                                        onChange={(e) => handleInputChange("paymentMode", e.target.value)}
                                                        className={`w-full px-3.5 py-2.5 text-sm font-normal rounded-xl border transition-all duration-200 outline-none ${
                                                            isDarkMode
                                                                ? 'bg-slate-800 border-slate-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                                                                : 'bg-white border-slate-200 text-slate-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/10'
                                                        }`}
                                                    >
                                                        <option value="">Select Payment</option>
                                                        <option value="prepaid">Prepaid</option>
                                                        <option value="cod">COD</option>
                                                    </select>
                                                </div>

                                                <div className="space-y-1.5">
                                                    <label className={`block text-xs font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                                                        Pickup Arrangement
                                                    </label>
                                                    <select
                                                        value={formData.pickupStatus}
                                                        onChange={(e) => handleInputChange("pickupStatus", e.target.value)}
                                                        className={`w-full px-3.5 py-2.5 text-sm font-normal rounded-xl border transition-all duration-200 outline-none ${
                                                            isDarkMode
                                                                ? 'bg-slate-800 border-slate-700 text-white focus:border-red-500 focus:ring-2 focus:ring-red-500/20'
                                                                : 'bg-white border-slate-200 text-slate-900 focus:border-red-500 focus:ring-2 focus:ring-red-500/10'
                                                        }`}
                                                    >
                                                        <option value="">Select Pickup</option>
                                                        <option value="fmpickup">FM Pickup</option>
                                                        <option value="selfdrop">Self Drop</option>
                                                    </select>
                                                </div>

                                                {formData.paymentMode === 'cod' && (
                                                    <div className="space-y-1.5">
                                                        <label className={`block text-xs font-medium ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                                                            COD Amount <span className="text-red-500">*</span>
                                                        </label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            value={formData.cod_amount}
                                                            onChange={(e) => handleInputChange('cod_amount', e.target.value)}
                                                            className={`w-full px-3.5 py-2.5 text-sm font-normal rounded-xl border transition-all duration-200 outline-none ${
                                                                isDarkMode
                                                                    ? 'bg-slate-800 border-amber-500/60 text-white focus:border-amber-500'
                                                                    : 'bg-amber-50/50 border-amber-300 text-slate-900 focus:border-amber-500'
                                                            }`}
                                                            placeholder="COD Amount"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* 4. Box Sets Section */}
                                        <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-slate-800/40 border-slate-800' : 'bg-slate-50/70 border-slate-200/80'} space-y-3`}>
                                            <div className="flex items-center justify-between gap-2 flex-wrap">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-6 h-6 rounded-lg bg-red-500/15 text-red-600 dark:text-red-400 flex items-center justify-center">
                                                        <Package className="w-3.5 h-3.5" />
                                                    </div>
                                                    <h3 className={`text-xs sm:text-sm font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                                        Box Dimensions & Package Sets
                                                    </h3>
                                                </div>

                                                {formData.totalBoxes && getRemainingBoxes() > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={addBoxSet}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white text-xs font-semibold rounded-xl shadow-xs transition-all duration-200 active:scale-95"
                                                    >
                                                        <Plus className="w-3.5 h-3.5" />
                                                        <span>Add Box Set</span>
                                                    </button>
                                                )}
                                            </div>

                                            {formData.totalBoxes && (
                                                <div className={`p-3 rounded-xl border flex items-center justify-between gap-3 text-xs flex-wrap ${
                                                    isDarkMode ? 'bg-slate-800/80 border-slate-700' : 'bg-white border-slate-200 shadow-2xs'
                                                }`}>
                                                    <span className="font-medium text-slate-600 dark:text-slate-300">
                                                        Total Required: <strong className="text-red-600 dark:text-red-400 font-semibold text-sm">{formData.totalBoxes}</strong>
                                                    </span>
                                                    <div className="flex items-center gap-2">
                                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                                                            Used: {getTotalBoxesUsed()}
                                                        </span>
                                                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                                                            getRemainingBoxes() === 0
                                                                ? 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800'
                                                                : 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800'
                                                        }`}>
                                                            Remaining: {getRemainingBoxes()}
                                                        </span>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Box Sets */}
                                            <div className="space-y-2.5">
                                                {boxes.map((box, index) => (
                                                    <div
                                                        key={box.id}
                                                        className={`p-3.5 rounded-2xl border transition-all duration-200 ${
                                                            isDarkMode
                                                                ? 'bg-slate-800/90 border-slate-700/80 hover:border-slate-600 shadow-xs'
                                                                : 'bg-white border-slate-200/90 hover:border-red-300 shadow-2xs hover:shadow-xs'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between mb-2.5">
                                                            <div className="flex items-center gap-2">
                                                                <span className="w-6 h-6 rounded-lg bg-red-100 dark:bg-red-950/50 text-red-700 dark:text-red-300 font-bold text-xs flex items-center justify-center">
                                                                    #{index + 1}
                                                                </span>
                                                                <h4 className={`text-xs sm:text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                                                                    Box Set {index + 1}
                                                                </h4>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeBoxSet(box.id)}
                                                                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-xl transition-all"
                                                                title="Remove Box Set"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </button>
                                                        </div>

                                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
                                                            <div className="space-y-1">
                                                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400">
                                                                    Quantity <span className="text-red-500">*</span>
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    max={getBoxMaxQuantity(index)}
                                                                    value={box.quantity}
                                                                    onChange={(e) => updateBoxSet(box.id, 'quantity', e.target.value)}
                                                                    className={`w-full px-3 py-1.5 text-sm font-normal rounded-xl border transition-all outline-none ${
                                                                        isDarkMode
                                                                            ? 'bg-slate-700/70 border-slate-600 text-white focus:border-red-500'
                                                                            : 'bg-slate-50/70 border-slate-200 text-slate-900 focus:border-red-500 focus:bg-white'
                                                                    }`}
                                                                    placeholder="Qty"
                                                                />
                                                            </div>

                                                            <div className="space-y-1">
                                                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400">
                                                                    Length (cm) <span className="text-red-500">*</span>
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={box.length}
                                                                    onChange={(e) => updateBoxSet(box.id, 'length', e.target.value)}
                                                                    className={`w-full px-3 py-1.5 text-sm font-normal rounded-xl border transition-all outline-none ${
                                                                        isDarkMode
                                                                            ? 'bg-slate-700/70 border-slate-600 text-white focus:border-red-500'
                                                                            : 'bg-slate-50/70 border-slate-200 text-slate-900 focus:border-red-500 focus:bg-white'
                                                                    }`}
                                                                    placeholder="L"
                                                                />
                                                            </div>

                                                            <div className="space-y-1">
                                                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400">
                                                                    Width (cm) <span className="text-red-500">*</span>
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={box.width}
                                                                    onChange={(e) => updateBoxSet(box.id, 'width', e.target.value)}
                                                                    className={`w-full px-3 py-1.5 text-sm font-normal rounded-xl border transition-all outline-none ${
                                                                        isDarkMode
                                                                            ? 'bg-slate-700/70 border-slate-600 text-white focus:border-red-500'
                                                                            : 'bg-slate-50/70 border-slate-200 text-slate-900 focus:border-red-500 focus:bg-white'
                                                                    }`}
                                                                    placeholder="W"
                                                                />
                                                            </div>

                                                            <div className="space-y-1">
                                                                <label className="block text-xs font-medium text-slate-500 dark:text-slate-400">
                                                                    Height (cm) <span className="text-red-500">*</span>
                                                                </label>
                                                                <input
                                                                    type="number"
                                                                    step="0.01"
                                                                    value={box.height}
                                                                    onChange={(e) => updateBoxSet(box.id, 'height', e.target.value)}
                                                                    className={`w-full px-3 py-1.5 text-sm font-normal rounded-xl border transition-all outline-none ${
                                                                        isDarkMode
                                                                            ? 'bg-slate-700/70 border-slate-600 text-white focus:border-red-500'
                                                                            : 'bg-slate-50/70 border-slate-200 text-slate-900 focus:border-red-500 focus:bg-white'
                                                                    }`}
                                                                    placeholder="H"
                                                                />
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </form>
                                </div>

                                {/* Sticky submit footer */}
                                <div className={`shrink-0 p-4 border-t ${isDarkMode ? 'border-slate-800 bg-slate-900/90' : 'border-slate-100 bg-white/90'} backdrop-blur-md`}>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={loading || !formData.originPincode || !formData.destinationPincode || !formData.totalWeight || !formData.totalBoxes || getTotalBoxesUsed() !== parseInt(formData.totalBoxes || 0)}
                                        className={`w-full py-3 px-5 rounded-xl text-sm font-semibold tracking-normal transition-all duration-200 shadow-md flex items-center justify-center gap-2 ${
                                            loading || !formData.originPincode || !formData.destinationPincode || !formData.totalWeight || !formData.totalBoxes || getTotalBoxesUsed() !== parseInt(formData.totalBoxes || 0)
                                                ? 'bg-slate-200 dark:bg-slate-800 text-slate-400 dark:text-slate-500 cursor-not-allowed shadow-none'
                                                : 'bg-red-600 hover:bg-red-700 active:scale-[0.99] text-white shadow-md shadow-red-600/30'
                                        }`}
                                    >
                                        {loading ? (
                                            <div className="flex items-center justify-center space-x-2">
                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                <span>Calculating Real-time Rates...</span>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-center space-x-2">
                                                <Sparkles className="w-4 h-4 text-amber-300" />
                                                <Calculator className="w-4 h-4" />
                                                <span>Calculate & Compare Carrier Rates</span>
                                            </div>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Sidebar Panel — scrolls internally */}
                        <div className="h-full overflow-hidden">
                            <div className={`${isDarkMode ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200/90'} h-full rounded-2xl sm:rounded-3xl shadow-xl shadow-slate-200/50 dark:shadow-black/40 border overflow-y-auto custom-scrollbar p-4 sm:p-5 space-y-4`}>

                                {/* Summary Card */}
                                <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-800' : 'bg-slate-50/70 border-slate-200/80'} space-y-3`}>
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-lg bg-red-500/15 text-red-600 dark:text-red-400 flex items-center justify-center">
                                            <MapPin className="w-3.5 h-3.5" />
                                        </div>
                                        <h3 className={`text-xs sm:text-sm font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                            Shipment Summary
                                        </h3>
                                    </div>

                                    <div className="space-y-2 text-xs divide-y divide-slate-100 dark:divide-slate-800">
                                        <div className="flex justify-between items-center pt-1.5">
                                            <span className="text-slate-500 dark:text-slate-400 font-normal">Origin:</span>
                                            <span className="font-semibold text-slate-800 dark:text-slate-200 truncate max-w-[150px] text-right">
                                                {originCity ? `${originCity} (${formData.originPincode})` : formData.originPincode || 'Not set'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center pt-2">
                                            <span className="text-slate-500 dark:text-slate-400 font-normal">Destination:</span>
                                            <span className="font-semibold text-slate-800 dark:text-slate-200 truncate max-w-[150px] text-right">
                                                {destinationCity ? `${destinationCity} (${formData.destinationPincode})` : formData.destinationPincode || 'Not set'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center pt-2">
                                            <span className="text-slate-500 dark:text-slate-400 font-normal">Cargo Weight:</span>
                                            <span className="font-semibold text-slate-800 dark:text-slate-200">
                                                {formData.totalWeight ? `${formData.totalWeight} kg` : 'Not set'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center pt-2">
                                            <span className="text-slate-500 dark:text-slate-400 font-normal">Total Boxes:</span>
                                            <span className="font-semibold text-red-600 dark:text-red-400">
                                                {formData.totalBoxes || 'Not set'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center pt-2">
                                            <span className="text-slate-500 dark:text-slate-400 font-normal">Insurance:</span>
                                            <span className="font-semibold text-slate-800 dark:text-slate-200 capitalize">
                                                {formData.insurance} Risk
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Box Details Card */}
                                {boxes.length > 0 && (
                                    <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-800' : 'bg-slate-50/70 border-slate-200/80'} space-y-3`}>
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-lg bg-red-500/15 text-red-600 dark:text-red-400 flex items-center justify-center">
                                                <Package className="w-3.5 h-3.5" />
                                            </div>
                                            <h3 className={`text-xs sm:text-sm font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                                Box Package Sets ({boxes.length})
                                            </h3>
                                        </div>

                                        <div className="space-y-2">
                                            {boxes.map((box, index) => (
                                                <div
                                                    key={box.id}
                                                    className={`p-2.5 rounded-xl border flex items-center justify-between text-xs ${
                                                        isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200/80 shadow-2xs'
                                                    }`}
                                                >
                                                    <div>
                                                        <span className="font-semibold text-slate-800 dark:text-slate-200">
                                                            Set #{index + 1}
                                                        </span>
                                                        <p className="text-xs text-slate-400 font-normal mt-0.5">
                                                            {box.length || 0} × {box.width || 0} × {box.height || 0} cm
                                                        </p>
                                                    </div>
                                                    <span className="px-2.5 py-1 rounded-lg bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 font-semibold text-xs border border-red-200 dark:border-red-800">
                                                        {box.quantity || 0} boxes
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Instructions Card */}
                                <div className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-slate-800/50 border-slate-800' : 'bg-slate-50/70 border-slate-200/80'} space-y-3`}>
                                    <div className="flex items-center gap-2">
                                        <div className="w-6 h-6 rounded-lg bg-red-500/15 text-red-600 dark:text-red-400 flex items-center justify-center">
                                            <Calculator className="w-3.5 h-3.5" />
                                        </div>
                                        <h3 className={`text-xs sm:text-sm font-semibold ${isDarkMode ? 'text-slate-200' : 'text-slate-800'}`}>
                                            How It Works
                                        </h3>
                                    </div>

                                    <div className="space-y-2.5 text-xs">
                                        <div className="flex items-start gap-2.5">
                                            <span className="w-5 h-5 rounded-full bg-red-600 text-white font-bold text-xs flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
                                                1
                                            </span>
                                            <span className="text-slate-600 dark:text-slate-300 font-normal leading-normal">Enter 6-digit origin & destination pincodes</span>
                                        </div>
                                        <div className="flex items-start gap-2.5">
                                            <span className="w-5 h-5 rounded-full bg-red-500 text-white font-bold text-xs flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
                                                2
                                            </span>
                                            <span className="text-slate-600 dark:text-slate-300 font-normal leading-normal">Specify weight, total boxes & declared value</span>
                                        </div>
                                        <div className="flex items-start gap-2.5">
                                            <span className="w-5 h-5 rounded-full bg-rose-600 text-white font-bold text-xs flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
                                                3
                                            </span>
                                            <span className="text-slate-600 dark:text-slate-300 font-normal leading-normal">Fill Length, Width, Height dimensions for all boxes</span>
                                        </div>
                                        <div className="flex items-start gap-2.5">
                                            <span className="w-5 h-5 rounded-full bg-red-700 text-white font-bold text-xs flex items-center justify-center shrink-0 mt-0.5 shadow-xs">
                                                4
                                            </span>
                                            <span className="text-slate-600 dark:text-slate-300 font-normal leading-normal">Click calculate to view instant partner rates</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <B2BViewRateForward
                isopen={isPopupOpen}
                onClose={handleClose}
                data={estimateData}
                type="checkRates"
            />
        </div>
    );
};

export default B2bRateCalculator;